import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Avatar, Badge, Box, Collapse, Divider, Paper, Typography } from '@mui/material';
import { css } from '@emotion/css';
import { PropertyDefinition } from '../types';
import { entryPropertyGetReadableValue, entryPropertyGetSecondaryValue, sortEntryByRank } from '../common/util';
import { TIMELINE_CARD_MIN_WIDTH, TIMELINE_ROW_HEIGHT, type TimelineCardExpansionMode } from '../utils/timelineUtils';
import { getDragSlideTilt } from '../utils/canvasAnchorUtils';
import type { RepositoryEntriesRowDto } from '../services/repositoryEntries';

export interface TimelineReferenceCardProps {
	row: RepositoryEntriesRowDto;
	idx: number,
	propertyDefinitions: PropertyDefinition[];
	datePropertyId: string | null;
	/** Current column offset (in the view's active scale unit) from the view's origin. */
	unitIndex: number;
	/** How far through its column (0–1) the entry's date falls; the card's left edge sits at that point. */
	unitOffset: number;
	/** Pixel width of one column at the view's active scale. */
	columnWidth: number;
	/** The view's visual zoom factor, used to convert on-screen pointer deltas into unzoomed px. */
	zoom: number;
	/** Whether/when properties beyond the title and date field are shown — see `TimelineCardExpansionMode`. */
	expansionMode: TimelineCardExpansionMode;
	collectionColor?: string;
	/** Shown bottom-right with the collection color dot, as on CanvasReferenceCard — set where a timeline mixes collections. */
	repositorySingular?: string;
	isSelected?: boolean;
	/** Suppresses drag-to-reschedule/reposition entirely — a pointer down just selects the card. */
	isPositionLocked?: boolean;
	/**
	 * Called once on release after a drag that moved the card. `nextUnitIndex` is the column under the
	 * pointer, or the card's own `unitIndex` while the pointer stays in the column it was grabbed in. `deltaY` is the vertical pointer delta (end minus start, in unzoomed px) — a relative offset, not
	 * an absolute screen position — so the parent can add it to the row's current y and get the new y
	 * without needing to know anything about where within the card the drag started (an absolute cursor
	 * position wouldn't line up with the card's own top edge).
	 */
	onReposition: (row: RepositoryEntriesRowDto, nextUnitIndex: number, deltaY: number) => void;
	onSelect?: (row: RepositoryEntriesRowDto) => void;
	/** Called once, the moment a pointer-down is promoted into an actual drag (crosses the move threshold). */
	onDragStart?: (row: RepositoryEntriesRowDto) => void;
	/** How many virtual cards to draw behind this one, when it fronts a collapsed stack. */
	stackDepth?: number;
}

/** Below this many px of movement (either axis), a pointer-down is treated as a click (select) rather than a drag. */
const DRAG_MOVE_THRESHOLD_PX = 4;

/** How many property definitions (besides the title and date fields) the expanded state can reveal. */
const MAX_EXTRA_PROPERTIES = 1;

const IS_AVATAR_ENABLED = false;
const MAX_STACK_DEPTH_LAYERS = 2;
const STACK_LAYER_INSET_PX = 6;
const STACK_LAYER_PEEK_PX = 5;

/**
 * One entry per card, freely positioned by TimelineView along both axes: horizontal drag reschedules
 * the entry's date, vertical drag moves it to any y position within the grid. Since the y position a
 * card lands on is resolved by the parent (from the grid's on-screen position), this card only tracks
 * the drag visually and reports the final pointer position on release — it never reflows sibling rows
 * live during the drag.
 */
export function TimelineReferenceCard({
	row,
	idx,
	propertyDefinitions,
	datePropertyId,
	unitIndex,
	unitOffset,
	columnWidth,
	zoom,
	expansionMode,
	collectionColor,
	repositorySingular,
	isSelected,
	isPositionLocked,
	onReposition,
	onSelect,
	onDragStart,
	stackDepth = 0,
}: TimelineReferenceCardProps) {
	const classes = {
		clamp2: css`
			overflow: hidden;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
		`,
		truncate: css`
			overflow: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;
		`,
	};
	const sortedDefs = [...propertyDefinitions].sort(sortEntryByRank);
	const firstDef = sortedDefs[0];
	const dateDef = sortedDefs.find((def) => def.id === datePropertyId);
	const extraDefs = sortedDefs
		.filter((def) => def.id !== firstDef?.id && def.id !== dateDef?.id)
		.slice(0, MAX_EXTRA_PROPERTIES);
	const title = firstDef ? entryPropertyGetReadableValue(firstDef, row.data?.[firstDef.slug]) : 'Untitled';
	const monogram = '🐍';//(title || 'Untitled').trim().charAt(0).toUpperCase();

	/**
	 * Pointer capture must be set/released on this stable node, never on `e.target` (whichever specific
	 * descendant happened to receive the pointerdown). If React ever replaces that descendant during a
	 * re-render, capture silently drops even though this outer element — matched by key, always moved
	 * rather than recreated — persists throughout.
	 */
	const cardRef = useRef<HTMLDivElement | null>(null);
	const positionerRef = useRef<HTMLDivElement | null>(null);
	/** `grabPosition` is the pointer's fractional column position at press, in the same local units as `unitIndex`. */
	const [dragStart, setDragStart] = useState<{ clientX: number; clientY: number; grabPosition: number } | null>(null);
	const [liveUnitIndex, setLiveUnitIndex] = useState(unitIndex);
	/** Raw, unrounded vertical pointer offset — drives a continuous transform so the card visibly tracks the cursor while dragging. */
	const [liveDy, setLiveDy] = useState(0);
	const hasMovedRef = useRef(false);

	useEffect(() => {
		setLiveUnitIndex(unitIndex);
	}, [unitIndex]);

	const handlePointerDown = useCallback(
		(e: React.PointerEvent) => {
			if ((e.target as HTMLElement).closest('button')) return;
			// Setting touch-action:none alone isn't always enough for the browser to commit to a drag
			// instead of a scroll on the first touch — preventDefault forces it, matching CanvasCardFrame.
			e.preventDefault();
			hasMovedRef.current = false;
			setLiveDy(0);
			const positionerLeft = (positionerRef.current as HTMLDivElement).getBoundingClientRect().left;
			const grabPosition = unitIndex + unitOffset + (e.clientX - positionerLeft) / zoom / columnWidth;
			setDragStart({ clientX: e.clientX, clientY: e.clientY, grabPosition });
			// A locked card leaves the pointer to the timeline background, which pans on the same gesture.
			if (!isPositionLocked) cardRef.current?.setPointerCapture?.(e.pointerId);
		},
		[isPositionLocked, unitIndex, unitOffset, zoom, columnWidth],
	);

	/**
	 * The column under the pointer. A card can be wider than a column, so the grab point may already sit in a
	 * later column than the card's own — staying within that grab column keeps the card's date, so a purely
	 * vertical drag never reschedules it.
	 */
	const pointerUnitIndex = useCallback(
		(clientX: number) => {
			if (!dragStart) return unitIndex;
			const pointerColumn = Math.floor(dragStart.grabPosition + (clientX - dragStart.clientX) / zoom / columnWidth);
			return pointerColumn === Math.floor(dragStart.grabPosition) ? unitIndex : pointerColumn;
		},
		[dragStart, unitIndex, zoom, columnWidth],
	);

	const handlePointerMove = useCallback(
		(e: React.PointerEvent) => {
			if (!dragStart) return;
			// Locked cards still accept pointer-down/up (so tap-to-select keeps working), but pointer
			// movement never promotes the gesture into a drag.
			if (isPositionLocked) return;
			const dx = e.clientX - dragStart.clientX;
			const dy = e.clientY - dragStart.clientY;
			if (!hasMovedRef.current) {
				if (Math.abs(dx) < DRAG_MOVE_THRESHOLD_PX && Math.abs(dy) < DRAG_MOVE_THRESHOLD_PX) return;
				hasMovedRef.current = true;
				onDragStart?.(row);
			}
			setLiveUnitIndex(pointerUnitIndex(e.clientX));
			setLiveDy(dy / zoom);
		},
		[dragStart, isPositionLocked, onDragStart, row, pointerUnitIndex, zoom],
	);

	/**
	 * The end-of-drag listener lives on `window`, not on this element's own onPointerUp: pointer capture
	 * retargets events but they still bubble to window, so this reliably fires for every real release —
	 * unlike a local handler, it can't be defeated by capture being silently lost mid-gesture. It also
	 * listens for `pointercancel` (fired when the browser interrupts a gesture, e.g. losing focus), which
	 * a local onPointerUp alone would never see.
	 */
	useEffect(() => {
		if (!dragStart) return;
		const endDrag = (e: PointerEvent) => {
			cardRef.current?.releasePointerCapture?.(e.pointerId);
			setDragStart(null);
			setLiveDy(0);
			if (hasMovedRef.current) {
				onReposition(row, pointerUnitIndex(e.clientX), (e.clientY - dragStart.clientY) / zoom);
			} else if (
				Math.abs(e.clientX - dragStart.clientX) < DRAG_MOVE_THRESHOLD_PX
				&& Math.abs(e.clientY - dragStart.clientY) < DRAG_MOVE_THRESHOLD_PX
			) {
				onSelect?.(row);
			}
		};
		window.addEventListener('pointerup', endDrag);
		window.addEventListener('pointercancel', endDrag);
		return () => {
			window.removeEventListener('pointerup', endDrag);
			window.removeEventListener('pointercancel', endDrag);
		};
	}, [dragStart, onReposition, onSelect, row, pointerUnitIndex, zoom]);

	// A locked card still records `dragStart` on press (for tap-to-select), but never renders as dragged: the
	// timeline pans during that gesture, so `liveUnitIndex` would lag one render behind the shifting window.
	const isDragging = Boolean(dragStart) && !isPositionLocked;
	const isRescheduling = isDragging && liveUnitIndex !== unitIndex;
	// A rescheduled entry lands on the start of its new column.
	const displayLeft = isRescheduling ? liveUnitIndex * columnWidth : (unitIndex + unitOffset) * columnWidth;
	const dragTilt = isDragging && hasMovedRef.current ? getDragSlideTilt(liveDy) : 0;
	const isArchived = Boolean(row.isArchived);

	const extraPropsBody = extraDefs.length > 0 && (
		<>
			<Divider sx={{ my: 1 }} />
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
				{extraDefs.map((def) => {
					const primaryValue = entryPropertyGetReadableValue(def, row.data?.[def.slug]);
					const secondaryValue = entryPropertyGetSecondaryValue(def, row.data?.[def.slug]);
					return (
						<Box key={def.id} sx={{ minWidth: 0 }}>
							<Typography variant="caption" color="text.secondary" className={classes.truncate} sx={{ display: 'block' }}>
								{def.label}
							</Typography>
							<Typography variant="body2" color="text.primary" className={classes.clamp2}>
								{primaryValue || '-'}
							</Typography>
							{secondaryValue && (
								<Typography variant="caption" color="primary.light">
									{secondaryValue}
								</Typography>
							)}
						</Box>
					);
				})}
			</Box>
		</>
	);

	return (
		<Box
			ref={positionerRef}
			sx={{
				position: 'absolute',
				top: 0,
				height: TIMELINE_ROW_HEIGHT,
				display: 'flex',
				alignItems: 'flex-start',
				zIndex: isDragging ? 2 : 1,
				// The card sits at the top of this full-row-height band, so it doesn't fill it —
				// without this, the empty space above/below the card would still swallow pointer events meant
				// for the background grid underneath (pan-to-scroll, slot selection) instead of passing through.
				pointerEvents: 'none',
			}}
			style={{
				left: displayLeft,
				transform: isDragging ? `translateY(${liveDy}px) rotate(${dragTilt}deg)` : undefined,
			}}
		>
			<Badge
				badgeContent={`+${stackDepth} more`}
				invisible={!stackDepth}
				color="primary"
				overlap="circular"
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			>
				<Paper
					ref={cardRef}
					data-timeline-card={row.id}
					onPointerDown={handlePointerDown}
					onPointerMove={handlePointerMove}
					sx={{
						pointerEvents: 'auto',
						display: 'flex',
						flexDirection: 'column',
						marginLeft: idx % 2 === 0 ? -0.25 : 0.25,
						p: (theme) => (repositorySingular ? theme.spacing(1, 1.5, 2) : theme.spacing(1, 1.5)),
						position: 'relative',
						// Oversized on purpose — CSS clamps a corner's radius to half the box's own (variable) height,
						// so this always yields a full pill regardless of how tall the content makes the card.
						borderRadius: 6,
						minWidth: TIMELINE_CARD_MIN_WIDTH,
						maxWidth: 280,
						cursor: isPositionLocked ? 'pointer' : isDragging ? 'grabbing' : 'grab',
						userSelect: 'none',
						touchAction: 'none',
						borderColor: isSelected ? 'primary.main' : 'transparent',
						boxShadow: (theme) => [
							theme.palette.mode === 'light' ? '0 2px 4px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1), 0 -2px 5px rgba(0,0,0,0.06)' : '0 2px 4px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1), 0 -2px 5px rgba(0,0,0,0.12)',
							// Each virtual card behind is a solid shadow, narrowed by a negative spread and pushed down further than
							// that spread, so only its bottom edge peeks out below the card in front of it.
							...Array.from({ length: Math.min(stackDepth, MAX_STACK_DEPTH_LAYERS) }, (_, i) => {
								const inset = (i + 1) * STACK_LAYER_INSET_PX;
								const offsetY = inset + (i + 1) * STACK_LAYER_PEEK_PX;
								return `0 ${offsetY}px 0 -${inset}px ${theme.palette.background.default}, 0 ${offsetY}px 3px -${inset - 1}px rgba(0,0,0,0.2)`;
							}),
						].join(', '),
						// Overrides the theme's default MuiPaper `backdrop-filter: blur(16px)` glass effect, which is
						// too expensive to composite at the card counts this view can reach.
						backdropFilter: 'none',
						WebkitBackdropFilter: 'none',
						backgroundColor: (theme) => theme.palette.background.default,
						// Border only delays on select, so it doesn't flash immediately; on deselect it drops
						// instantly since the browser reads the transition off the state being transitioned into.
						transition: isSelected ? 'transform 0.2s ease, border-color 0s' : 'transform 0.2s ease, border-color 0s',
						// Clips the extra-properties section's collapse animation to the card's rounded outline.
						overflow: 'hidden',
						transform: isSelected ? 'scale(1.1)' : 'none',
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
						{IS_AVATAR_ENABLED && <Avatar
							sx={{
								ml: '-9px',
								mt: '-9px',
								width: 48,
								height: 48,
								flexShrink: 0,
								fontSize: 24,
								fontWeight: 600,
								bgcolor: 'transparent',
								color: (theme) => theme.palette.getContrastText(collectionColor || theme.palette.grey[700]),
							}}
						>
							{monogram}
						</Avatar>}
						<Box sx={{ minWidth: 0, flex: 1 }}>
							<Typography
								variant="body2"
								color="text.primary"
								className={classes.clamp2}
								sx={{ textDecoration: isArchived ? 'line-through' : 'none', fontWeight: 500 }}
							>
								{title || 'Untitled'}
							</Typography>
						</Box>
					</Box>
					{repositorySingular && (
						<Box display="flex" alignItems="center" gap={0.5} sx={{ position: 'absolute', bottom: 0, right: 12 }}>
							<Typography color="action.disabled" variant="caption" sx={{ fontSize: '0.625rem', lineHeight: 1.5 }}>{repositorySingular}</Typography>
							{collectionColor && <Box sx={{ mt: -1, width: 6, height: 6, borderRadius: '50%', backgroundColor: collectionColor }} />}
						</Box>
					)}
					{expansionMode === 'expanded' && extraPropsBody}
					{expansionMode === 'expandable' && (
						<Collapse in={Boolean(isSelected) && extraDefs.length > 0} timeout="auto" unmountOnExit>
							{extraPropsBody}
						</Collapse>
					)}
				</Paper>
			</Badge>
		</Box>
	);
}

/**
 * Memoized so pure panning (which re-renders TimelineView on every animation frame via `panX`) doesn't
 * also re-render every card — `unitIndex`/`row`/the callbacks below are all kept referentially stable
 * across pan frames by TimelineView specifically so this bail-out holds.
 */
export default React.memo(TimelineReferenceCard);
